import React, { createContext, useState, useEffect } from 'react';
import { api, axios } from '../utils/requestInterceptor';
import jwt_decode from 'jwt-decode';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwt_decode(token);
        const currentTime = Date.now() / 1000;
        
        if (decodedToken.exp < currentTime) {
          // Token is expired
          localStorage.removeItem('token');
          setCurrentUser(null);
          setIsAuthenticated(false);
        } else {
          // Set axios default headers first
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Fetch complete user information from /me endpoint instead of using token decode
          fetchCurrentUserInfo(token);
        }
      } catch (error) {
        console.error('Token decode error:', error);
        localStorage.removeItem('token');
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
    
    // Add mock token in development mode if using mock data
    if (process.env.NODE_ENV === 'development' && !token) {
      console.log('Setting mock token for development');
      const mockToken = 'mock_jwt_token_for_development';
      localStorage.setItem('token', mockToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${mockToken}`;
      
      // Set a mock user for development
      const mockUser = {
        id: '12345',
        name: 'Test User',
        email: 'test@example.com',
        role_type: 'admin'
      };
      setCurrentUser(mockUser);
      setIsAuthenticated(true);
    }
  }, []);

  // Helper function to fetch complete user information
  const fetchCurrentUserInfo = async (token) => {
    try {
      console.log('Fetching complete user info from /me endpoint');
      const response = await api.get('/auth/me');
      console.log('User info response:', response.data);
      
      if (response.data.user) {
        // Map the response to match expected format
        const user = {
          ...response.data.user,
          role_type: response.data.user.role // Map 'role' to 'role_type' for consistency
        };
        setCurrentUser(user);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      // If fetching user info fails, fall back to token decode
      try {
        const decodedToken = jwt_decode(token);
        setCurrentUser(decodedToken);
        setIsAuthenticated(true);
      } catch (decodeError) {
        console.error('Token decode fallback failed:', decodeError);
        localStorage.removeItem('token');
        setCurrentUser(null);
        setIsAuthenticated(false);
      }
    }
  };

  const login = async (credentials) => {
    try {
      setError('');
      // For development, we'll use a mock login
      if (process.env.NODE_ENV === 'development') {
        const mockUser = {
          id: '12345',
          name: 'Test User',
          email: credentials.email || 'test@example.com',
          role_type: 'admin'
        };
        
        const mockToken = 'mock_jwt_token_for_development';
        localStorage.setItem('token', mockToken);
        setCurrentUser(mockUser);
        setIsAuthenticated(true);
        axios.defaults.headers.common['Authorization'] = `Bearer ${mockToken}`;
        return mockUser;
      }
      
      // For production
      const response = await api.post('/auth/login', credentials);
      const { token } = response.data;
      
      localStorage.setItem('token', token);
      const user = jwt_decode(token);
      setCurrentUser(user);
      
      // Set axios default headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      return user;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to log in');
      throw err;
    }
  };

  const googleLogin = async (tokenId) => {
    try {
      console.log('Starting Google login process with token');
      setError('');
      
      // For real Google token
      console.log('Calling /auth/google endpoint');
      const response = await api.post('/auth/google', { token: tokenId });
      console.log('Google auth response:', response.data);
      
      const { token, user } = response.data;
      
      console.log('Storing token in localStorage');
      localStorage.setItem('token', token);
      
      console.log('Setting current user state');
      setCurrentUser(user);
      
      console.log('Setting isAuthenticated to true');
      setIsAuthenticated(true);
      
      // Set axios default headers
      console.log('Setting Authorization header for future requests');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      return user;
    } catch (err) {
      console.error('Google login error:', err);
      setError(err.response?.data?.message || 'Google authentication failed');
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
  };

  const value = {
    currentUser,
    loading,
    error,
    setError,
    login,
    googleLogin,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 